import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { components } from "@qorakul/shared/api";
import { api } from "./api.ts";

export type UnmarkedLessonItem = components["schemas"]["UnmarkedLessonItem"];
export type ReminderSettingsDetails = components["schemas"]["ReminderSettingsDetails"];

export interface UnmarkedLessonFilters {
  from: string | undefined;
  to: string | undefined;
  teacherId: string | undefined;
  classId: string | undefined;
  includeCleared: boolean;
  page: number;
  pageSize: number;
}

export const unmarkedKeys = {
  all: ["attendance", "unmarked"] as const,
  list: (filters: UnmarkedLessonFilters) => ["attendance", "unmarked", "list", filters] as const,
  settings: ["attendance", "reminder-settings"] as const,
};

/** One page of the lessons flagged at their marking deadline (GET /attendance/unmarked-lessons). */
export function useUnmarkedLessons(filters: UnmarkedLessonFilters) {
  const { t } = useTranslation();
  return useQuery({
    queryKey: unmarkedKeys.list(filters),
    queryFn: async () => {
      const query = {
        ...(filters.from ? { From: filters.from } : {}),
        ...(filters.to ? { To: filters.to } : {}),
        ...(filters.teacherId ? { TeacherId: filters.teacherId } : {}),
        ...(filters.classId ? { ClassId: filters.classId } : {}),
        IncludeCleared: filters.includeCleared,
        Page: filters.page,
        PageSize: filters.pageSize,
      };
      const { data, error } = await api.GET("/attendance/unmarked-lessons", { params: { query } });
      if (error || !data) throw new Error(t("app.error"));
      return data;
    },
    placeholderData: keepPreviousData,
  });
}

/** The teacher reminder times (GET /attendance/reminder-settings). */
export function useReminderSettings() {
  const { t } = useTranslation();
  return useQuery({
    queryKey: unmarkedKeys.settings,
    queryFn: async () => {
      const { data, error } = await api.GET("/attendance/reminder-settings");
      if (error || !data) throw new Error(t("app.error"));
      return data;
    },
  });
}

/** "18:00:00" → "18:00". */
export function formatTime(value: string | null | undefined): string {
  return value ? value.slice(0, 5) : "";
}
